import { useCallback, useEffect, useState } from 'react';

const DISMISSED_KEY = 'wadakaroo.installPromptDismissedAt';

// How long a dismissal suppresses the prompt. Long enough that it isn't
// nagging, short enough that a tech who dismissed it once still gets offered
// the install after switching phones or clearing the app.
const DISMISS_TTL_MS = 30 * 24 * 60 * 60 * 1000; // 30 days

/**
 * True when the app is already running as an installed PWA, in which case
 * there is nothing to prompt for. `display-mode: standalone` covers Android and
 * desktop; `navigator.standalone` is the iOS Safari equivalent.
 */
export const isStandalone = () => {
    if (typeof window === 'undefined') return false;
    return window.matchMedia?.('(display-mode: standalone)').matches
        || window.navigator.standalone === true;
};

/**
 * iOS Safari never fires beforeinstallprompt — it needs Share → Add to Home Screen.
 *
 * Exported because the notifications screen needs the same answer for a different
 * reason: on iPhone, web push works ONLY inside an installed PWA, so "install this"
 * is the fix there rather than a convenience. Sharing the detection keeps one
 * definition of "is this an iPhone" — the iPadOS-reports-as-a-Mac case below is
 * exactly the sort of thing a second copy gets wrong.
 */
export const isIos = () => {
    if (typeof navigator === 'undefined') return false;
    return /iphone|ipad|ipod/i.test(navigator.userAgent)
        // iPadOS 13+ reports as a Mac; the touch points check separates it from a desktop.
        || (/macintosh/i.test(navigator.userAgent) && navigator.maxTouchPoints > 1);
};

const wasRecentlyDismissed = () => {
    try {
        const raw = window.localStorage.getItem(DISMISSED_KEY);
        if (!raw) return false;
        return Date.now() - Number(raw) < DISMISS_TTL_MS;
    } catch {
        // Safari private mode throws on localStorage access. Treat it as
        // "never dismissed" — showing the prompt is the harmless failure.
        return false;
    }
};

/**
 * useInstallPrompt — drives the "Add Wadakaroo to your home screen" card.
 *
 * Two very different platforms behind one interface:
 *   • Chrome / Edge / Samsung fire `beforeinstallprompt`, which we capture and
 *     replay from a button. Note the event may fire BEFORE React mounts, which
 *     is why the listener is attached in a module-level effect on first use and
 *     the captured event is stored rather than acted on immediately.
 *   • iOS Safari has no API at all, so `instructionsOnly` is returned and the
 *     UI explains the Share-sheet route instead of rendering an Install button.
 */
const useInstallPrompt = () => {
    const [deferredEvent, setDeferredEvent] = useState(null);
    const [dismissed, setDismissed] = useState(wasRecentlyDismissed);
    const [installed, setInstalled] = useState(isStandalone);

    useEffect(() => {
        const onBeforeInstallPrompt = (e) => {
            // Chrome shows its own mini-infobar unless the event is cancelled.
            e.preventDefault();
            setDeferredEvent(e);
        };
        const onInstalled = () => {
            setInstalled(true);
            setDeferredEvent(null);
        };

        window.addEventListener('beforeinstallprompt', onBeforeInstallPrompt);
        window.addEventListener('appinstalled', onInstalled);
        return () => {
            window.removeEventListener('beforeinstallprompt', onBeforeInstallPrompt);
            window.removeEventListener('appinstalled', onInstalled);
        };
    }, []);

    const dismiss = useCallback(() => {
        setDismissed(true);
        try {
            window.localStorage.setItem(DISMISSED_KEY, String(Date.now()));
        } catch { /* private mode — the in-memory dismissal still holds for this session */ }
    }, []);

    const promptInstall = useCallback(async () => {
        if (!deferredEvent) return false;
        deferredEvent.prompt();
        const choice = await deferredEvent.userChoice.catch(() => null);
        // The event is single-use; a second prompt() throws.
        setDeferredEvent(null);
        if (choice?.outcome !== 'accepted') dismiss();
        return choice?.outcome === 'accepted';
    }, [deferredEvent, dismiss]);

    const instructionsOnly = isIos() && !installed;

    return {
        // Show the card when the browser offered an install, or on iOS where we
        // can only give instructions. Never once already installed or dismissed.
        canShow: !installed && !dismissed && (Boolean(deferredEvent) || instructionsOnly),
        instructionsOnly,
        installed,
        promptInstall,
        dismiss,
    };
};

export default useInstallPrompt;
